import React, { useMemo, useState } from "react";
import { styled } from "styled-components";
import { tokens } from "./tokens";
import { Eyebrow } from "./styled";
import NameToggle from "./NameToggle";
import FilterChips from "./FilterChips";
import VehicleTable from "./VehicleTable";
import { matchesQuery } from "./fleetSearch";
import { ConfigSchema } from "./configTypes";
import { Filter, isTestRig, NameMode, Vehicle } from "./data";

/**
 * The fleet, as one table.
 *
 * This used to offer a card grid and a table behind a segmented control, and
 * defaulted to the cards. They were not two ways of reading the same thing: a
 * card could show one tractor's headline and about six of its fields, while the
 * question this console exists to answer — "which tractors are approved for
 * autonomous production ops, and which of those are grounded" — is a column read
 * down the whole fleet. The cards could not do that at any width, so the toggle
 * mostly recorded that someone had discovered the table.
 *
 * What the cards did carry that the table did not was the open-ticket severity
 * breakdown; that now lives in the table's Open tickets column, so nothing was
 * dropped along with them (SW-6665).
 *
 * Layout is a two-part column: a masthead sized by its content, and the table
 * taking everything left. That is what lets the table be exactly as tall as the
 * window allows without anyone hardcoding how tall the chrome above it is —
 * see VehicleTable's own note for the width half of the same argument.
 *
 * The masthead carries no title of its own. The page header above already says
 * "Tractor State", and a second 34px heading reading "Fleet" under it cost the
 * table a row and a half on a laptop for a word the reader had just read. What
 * survives is what the heading's subtitle carried — where the data is from —
 * beside the two counts and the one rare action.
 */
interface FleetViewProps {
  vehicles: Vehicle[];
  schema: ConfigSchema | null;
  /** Describes where the data came from, rendered in the masthead. */
  subtitle: string;
  filter: Filter;
  onFilter: (f: Filter) => void;
  nameMode: NameMode;
  onNameMode: (m: NameMode) => void;
  /** Provided only when this caller may onboard a tractor. */
  onAddTractor?: () => void;
  /** Provided only when this caller may edit the schema (internal fleet-admin). */
  onEditSchema?: () => void;
  /** Whether test rigs and simulators are listed with the tractors. */
  showRigs: boolean;
  onShowRigs: (show: boolean) => void;
  onOpen: (vehicleName: string) => void;
}

const FleetView: React.FC<FleetViewProps> = ({
  vehicles,
  schema,
  subtitle,
  filter,
  onFilter,
  nameMode,
  onNameMode,
  onAddTractor,
  onEditSchema,
  showRigs,
  onShowRigs,
  onOpen,
}) => {
  const [query, setQuery] = useState("");

  // Rigs and simulators share the roster with the tractors but are not what
  // "how is the fleet" asks about; they stay out of the list unless asked for.
  const rigCount = useMemo(() => vehicles.filter(isTestRig).length, [vehicles]);
  const listed = useMemo(
    () => (showRigs ? vehicles : vehicles.filter((v) => !isTestRig(v))),
    [vehicles, showRigs],
  );

  const byCustomer = useMemo(
    () => listed.filter((v) => filter === "all" || v.customer === filter),
    [listed, filter],
  );

  /**
   * Rows in the order the API sent them — most actionable first, per
   * `operational_state.attention_rank`. Deliberately not re-sorted here: the
   * backend owns that precedence, and any other order the reader wants is a
   * click on the column that holds it.
   */
  const rows = useMemo(
    () =>
      query.trim()
        ? byCustomer.filter((v) => matchesQuery(v, schema, nameMode, query))
        : byCustomer,
    [byCustomer, query, schema, nameMode],
  );

  const openTotal = useMemo(
    () => rows.reduce((n, v) => n + v.openTicketCount, 0),
    [rows],
  );

  return (
    <FleetLayout>
      <Masthead>
        <HeaderRow>
          <Source>{subtitle}</Source>
          <HeaderRight>
            <Stats aria-label="Totals for the tractors shown">
              <Stat>
                <StatNum>{rows.length}</StatNum>
                <Eyebrow>Tractors</Eyebrow>
              </Stat>
              <Stat>
                <StatNum>{openTotal}</StatNum>
                <Eyebrow>Open tickets</Eyebrow>
              </Stat>
            </Stats>
            {onEditSchema && (
              <AddButton type="button" onClick={onEditSchema}>
                Schema
              </AddButton>
            )}
            {onAddTractor && (
              <AddButton type="button" onClick={onAddTractor}>
                + Add tractor
              </AddButton>
            )}
          </HeaderRight>
        </HeaderRow>

        <Controls>
          <FilterChips vehicles={listed} filter={filter} onFilter={onFilter} />
          {rigCount > 0 && (
            <RigToggle
              type="button"
              aria-pressed={showRigs}
              onClick={() => onShowRigs(!showRigs)}
              title="Test rigs and simulators share the roster but are not tractors"
            >
              <Switch $on={showRigs} aria-hidden="true" />
              Show test rigs <b>{rigCount}</b>
            </RigToggle>
          )}
          <RightGroup>
            <Search
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter tractors…"
              aria-label="Filter tractors"
            />
            <Count role="status">
              {query.trim()
                ? `${rows.length} of ${byCustomer.length} shown`
                : `${byCustomer.length} tractors`}
            </Count>
            <NameToggle value={nameMode} onChange={onNameMode} />
          </RightGroup>
        </Controls>
      </Masthead>

      <VehicleTable
        vehicles={rows}
        schema={schema}
        nameMode={nameMode}
        onOpen={onOpen}
        query={query.trim()}
        onClearQuery={() => setQuery("")}
        sizeNamesFrom={vehicles}
      />
    </FleetLayout>
  );
};

/**
 * The page column: masthead, then table.
 *
 * `flex: 1; min-height: 0` claims the height its parent has left, and hands all
 * of it that the masthead doesn't use to the table. No max-width — a fleet table
 * is the one thing on this console that genuinely reads better the wider it
 * gets, and capping it left an ultrawide display with more empty pixels beside
 * the data than data.
 */
const FleetLayout = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  padding: 22px 40px 28px;

  @media (max-width: 760px) {
    padding: 16px 16px 24px;
  }
`;

/** Sized by its content, so the table below gets the rest and not a share. */
const Masthead = styled.div`
  flex: none;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

const Source = styled.div`
  font-size: 13.5px;
  color: ${tokens.slate};
  align-self: center;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 22px;
  flex-wrap: wrap;
`;

// Onboarding is fleet-admin work and rare, so it sits beside the counters
// rather than competing with the filter controls ops use every day.
const AddButton = styled.button`
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  border-radius: 999px;
  padding: 8px 16px;
  cursor: pointer;
  background: ${tokens.white};
  border: 1px solid ${tokens.line};
  color: ${tokens.ink};

  &:hover {
    border-color: ${tokens.blue};
    color: ${tokens.blue};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }
`;

const Stats = styled.div`
  display: flex;
  gap: 24px;
`;

const Stat = styled.div``;

const StatNum = styled.div`
  font-size: 26px;
  font-weight: 200;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${tokens.ink};
  margin-bottom: 4px;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

const Search = styled.input`
  font-family: inherit;
  font-size: 13px;
  padding: 7px 12px;
  min-width: 240px;
  border: 1px solid ${tokens.inputLine};
  border-radius: 999px;
  background: ${tokens.white};
  color: ${tokens.ink};

  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 1px;
    border-color: ${tokens.blue};
  }
`;

const Count = styled.span`
  font-size: 12px;
  color: ${tokens.slate};
  white-space: nowrap;
`;

// Search + count + name toggle, right-aligned as a unit that wraps below the
// filter chips on narrow screens.
const RigToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 6px;
  padding: 6px 12px;
  border: 1px dashed ${tokens.toggleOff};
  border-radius: 999px;
  background: transparent;
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  color: ${tokens.slate};
  cursor: pointer;

  b {
    color: ${tokens.ink};
  }
  &[aria-pressed="true"] {
    border-style: solid;
    border-color: ${tokens.blue};
    color: ${tokens.ink};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }
`;

const Switch = styled.span<{ $on: boolean }>`
  position: relative;
  width: 28px;
  height: 16px;
  border-radius: 999px;
  background: ${(p) => (p.$on ? tokens.blue : tokens.toggleOff)};
  transition: background 0.12s;

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$on ? "14px" : "2px")};
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${tokens.white};
    transition: left 0.12s;
  }
`;

const RightGroup = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

export default FleetView;
